import React, { createContext, useContext, useState, useEffect } from 'react';
import useInstanceStore from '../stores/instanceStore';
import { getAuthHeader, getUserIdFromToken } from '../services/authToken';

const ProcessingContext = createContext();

export const useProcessing = () => {
  const context = useContext(ProcessingContext);
  if (!context) {
    throw new Error('useProcessing must be used within ProcessingProvider');
  }
  return context;
};

export const ProcessingProvider = ({ children }) => {
  const [processingJobs, setProcessingJobs] = useState(new Map());
  const [websocket, setWebsocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  // Enhanced processing stages with detailed information
  const PROCESSING_STAGES = {
    'uploaded': { label: 'Uploaded', progress: 5, color: 'bg-blue-500', description: 'Document uploaded and queued' },
    'validation': { label: 'Validating...', progress: 10, color: 'bg-blue-600', description: 'Validating input data' },
    'parsing': { label: 'Extracting Text...', progress: 20, color: 'bg-yellow-500', description: 'Parsing document content' },
    'ai_generation': { label: 'AI Processing...', progress: 40, color: 'bg-orange-500', description: 'Generating content with AI' },
    'image_generation': { label: 'Creating Visuals...', progress: 60, color: 'bg-purple-500', description: 'Generating images and media' },
    'entity_extraction': { label: 'Finding Characters...', progress: 75, color: 'bg-indigo-500', description: 'Extracting entities and keywords' },
    'content_processing': { label: 'Processing Content...', progress: 80, color: 'bg-violet-500', description: 'Validating and structuring content' },
    'database_save': { label: 'Saving...', progress: 90, color: 'bg-green-400', description: 'Saving to database' },
    'finalization': { label: 'Finalizing...', progress: 95, color: 'bg-green-500', description: 'Completing processing' },
    'completed': { label: 'Complete!', progress: 100, color: 'bg-green-600', description: 'Processing completed successfully' },
    'failed': { label: 'Failed', progress: 0, color: 'bg-red-500', description: 'Processing failed' }
  };

  useEffect(() => {
    // Initialize WebSocket connection for real-time updates
    const connectWebSocket = () => {
      try {
        // Use the correct backend URL and port for WebSocket
        const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('http', 'ws') + '/ws' || 'ws://localhost:8001/ws';
        const ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('📡 Processing WebSocket connected to:', wsUrl);
          setWebsocket(ws);
          setConnectionStatus('connected');
          setReconnectAttempts(0);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📦 Processing update received:', data);
            
            // Handle both old and new event types
            if (data.type === 'processing_update' && data.documentId) {
              updateJobStatus(data.documentId, data.stage, data.progress, data.error);
            } else if (data.type === 'progress-update') {
              // New real-time progress updates
              if (data.documentId) {
                updateJobStatus(data.documentId, data.stage, data.progress, data.error);
              }
              // Also update instance-level progress if available
              if (data.instanceId) {
                updateInstanceProgress(data.instanceId, data.stage, data.progress, data.description);
              }
            } else if (data.type === 'instance_update') {
              // Instance-level status updates
              updateInstanceProgress(data.instanceId, data.status, data.progress);
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        ws.onclose = () => {
          console.log('📡 Processing WebSocket disconnected');
          setWebsocket(null);
          setConnectionStatus('disconnected');
          
          // Exponential backoff for reconnection
          const backoffTime = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
          console.log(`📡 Reconnecting in ${backoffTime}ms (attempt ${reconnectAttempts + 1})`);
          
          setTimeout(() => {
            setReconnectAttempts(prev => prev + 1);
            setConnectionStatus('reconnecting');
            connectWebSocket();
          }, backoffTime);
        };

        ws.onerror = (error) => {
          console.error('📡 WebSocket error:', error);
          setConnectionStatus('disconnected');
        };

      } catch (error) {
        console.error('Failed to connect WebSocket:', error);
        // Fallback to polling without WebSocket
        console.log('📡 Using polling fallback instead of WebSocket');
      }
    };

    // Try WebSocket connection, but don't block if it fails
    connectWebSocket();

    return () => {
      if (websocket) {
        websocket.close();
      }
    };
  }, []);

  const startProcessing = (documentId, initialStage = 'uploaded', instanceId = null) => {
    console.log(`🚀 Starting processing for document ${documentId}, instance ${instanceId}`);
    setProcessingJobs(prev => new Map(prev.set(documentId, {
      documentId,
      instanceId,
      stage: initialStage,
      progress: PROCESSING_STAGES[initialStage]?.progress || 0,
      startTime: Date.now(),
      error: null,
      isActive: true
    })));

    // Immediately update instance status to show processing
    if (instanceId) {
      updateInstanceProgress(
        instanceId, 
        initialStage, 
        PROCESSING_STAGES[initialStage]?.progress || 0,
        PROCESSING_STAGES[initialStage]?.description || 'Processing started'
      );
    }

    // Start polling for status updates if WebSocket is not available
    if (!websocket || connectionStatus !== 'connected') {
      console.log('📊 Starting fallback polling for document:', documentId, 'WebSocket status:', connectionStatus);
      startPolling(documentId);
    }

    // Set a timeout to detect truly stuck processing (not fake progression)
    setTimeout(() => {
      const job = processingJobs.get(documentId);
      if (job && job.isActive && job.stage === 'uploaded') {
        console.log('⚠️  Processing appears stuck at upload stage - this may indicate a backend issue');
        // Don't auto-progress - let real status polling handle this
      }
    }, 30000); // Wait 30 seconds before considering it truly stuck
  };

  const startPolling = (documentId) => {
    let attempts = 0;
    const maxAttempts = 60; // 2 minutes of polling at 2-second intervals
    
    const pollInterval = setInterval(async () => {
      attempts++;
      
      try {
        // First try to get real-time progress for this specific document
        const progressResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002'}/api/documents/${documentId}/progress`,
            {
              headers: getAuthHeader()
            }
          );
        
        if (progressResponse.ok) {
          const progressData = await progressResponse.json();
          
          // Update with real progress from backend
          if (progressData.progress !== undefined) {
            updateJobStatus(documentId, progressData.stage, progressData.progress, 
              progressData.status === 'failed' ? 'Processing failed' : null);
            
            if (progressData.status === 'completed' || progressData.status === 'content_generated') {
              clearInterval(pollInterval);
              return;
            }
          }
        }
        
        // Fallback to the general processing-status endpoint
        const userId = getUserIdFromToken();
        if (!userId) {
          console.warn('⚠️ No user ID available for progress polling');
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002'}/api/documents/processing-status?user_id=${userId}`, {
            headers: getAuthHeader()
          });
        
        if (response.ok) {
          const data = await response.json();
          const { failed_documents = [] } = data;
          
          // Check if our document failed
          const failedDoc = failed_documents.find(doc => doc.id === documentId);
          if (failedDoc) {
            updateJobStatus(documentId, 'failed', 0, failedDoc.error || 'Processing failed');
            clearInterval(pollInterval);
            return;
          }
          
          // If no failure detected and we've been polling for a while, check for success
          if (attempts > 10) {
            // Try to determine if processing completed by checking for generated content
            try {
              const docResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002'}/api/documents/${documentId}`);
              if (docResponse.ok) {
                const docData = await docResponse.json();
                if (docData.document && docData.document.instance_id) {
                  const feedResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002'}/api/feed/${docData.document.instance_id}?limit=5`);
                  if (feedResponse.ok) {
                    const feedData = await feedResponse.json();
                    if (feedData.posts && feedData.posts.length > 0) {
                      updateJobStatus(documentId, 'completed', 100);
                      clearInterval(pollInterval);
                      return;
                    }
                  }
                }
              }
            } catch (contentCheckError) {
              console.log('Still waiting for content generation...');
            }
          }
          
          // GAP-UI-001 FIX: Removed fallback fake progress estimation
          // Backend now provides real progress updates via WebSocket and polling,
          // so we don't need time-based guessing anymore
        }
      } catch (error) {
        console.error('Error polling job status:', error);
      }
      
      // Stop polling after max attempts
      if (attempts >= maxAttempts) {
        updateJobStatus(documentId, 'failed', 0, 'Processing timeout - check if your document requires OCR processing');
        clearInterval(pollInterval);
      }
    }, 2000); // Poll every 2 seconds
  };

  const updateJobStatus = (documentId, stage, customProgress = null, error = null) => {
    console.log(`📊 Updating job ${documentId}: ${stage}`, { customProgress, error });
    setProcessingJobs(prev => {
      const updated = new Map(prev);
      const existing = updated.get(documentId) || {};
      const stageData = PROCESSING_STAGES[stage];
      
      updated.set(documentId, {
        ...existing,
        documentId,
        stage,
        progress: customProgress !== null ? customProgress : (stageData?.progress || existing.progress || 0),
        error,
        isActive: stage !== 'completed' && stage !== 'failed',
        lastUpdate: Date.now()
      });
      
      return updated;
    });
  };

  const updateInstanceProgress = (instanceId, stage, progress, description) => {
    console.log(`🏗️ Updating instance ${instanceId}: ${stage} (${progress}%)`, { description });
    
    // Update the instance store directly for immediate UI updates
    const { updateInstance } = useInstanceStore.getState();
    
    // Determine status based on stage
    const status = stage === 'completed' ? 'ready' : 
                  stage === 'failed' ? 'failed' : 'processing';
    
    // Update instance with processing state
    updateInstance(instanceId, {
      status,
      progress: Math.round(progress || 0),
      processing_stage: stage,
      processing_description: description
    });
    
    // Also broadcast instance update to other components via custom event for backward compatibility
    window.dispatchEvent(new CustomEvent('instance-progress-update', {
      detail: {
        instanceId,
        stage,
        progress,
        description,
        timestamp: Date.now()
      }
    }));
  };

  const getJobStatus = (documentId) => {
    return processingJobs.get(documentId);
  };

  const removeJob = (documentId) => {
    const job = processingJobs.get(documentId);
    
    setProcessingJobs(prev => {
      const updated = new Map(prev);
      updated.delete(documentId);
      return updated;
    });
    
    // Also update instance status if job was processing
    if (job && job.instanceId && job.isActive) {
      console.log(`🗑️ Removing processing job and updating instance ${job.instanceId} status`);
      updateInstanceProgress(job.instanceId, 'cancelled', 0, 'Processing cancelled by user');
      
      // Reset instance to ready state after a brief delay
      setTimeout(() => {
        const { updateInstance } = useInstanceStore.getState();
        updateInstance(job.instanceId, {
          status: 'ready',
          progress: 0,
          processing_stage: null,
          processing_description: null
        });
      }, 1000);
    }
  };

  const cancelProcessing = async (documentId) => {
    console.log(`🛑 Cancelling processing for document ${documentId}`);
    
    try {
      // Try to cancel on the backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002'}/api/documents/cancel/${documentId}`, {
          method: 'POST',
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'application/json'
          }
        });
      
      if (response.ok) {
        console.log('✅ Processing cancelled on backend');
      } else {
        console.warn('⚠️ Backend cancellation failed, removing job locally');
      }
    } catch (error) {
      console.error('❌ Error cancelling on backend:', error);
    }
    
    // Remove job locally regardless of backend response
    removeJob(documentId);
  };

  const getActiveJobs = () => {
    return Array.from(processingJobs.values()).filter(job => job.isActive);
  };

  const getAllJobs = () => {
    return Array.from(processingJobs.values());
  };

  const getStageInfo = (stage) => {
    return PROCESSING_STAGES[stage] || { label: stage, progress: 0, color: 'bg-gray-500' };
  };

  // Fallback polling for when WebSocket is not available
  const pollJobStatus = async (documentId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${documentId}/status`);
      if (response.ok) {
        const data = await response.json();
        updateJobStatus(documentId, data.stage, data.progress, data.error);
      }
    } catch (error) {
      console.error('Error polling job status:', error);
    }
  };

  // Manual reconnection function
  const reconnectWebSocket = () => {
    if (websocket) {
      websocket.close();
    }
    setReconnectAttempts(0);
    setConnectionStatus('reconnecting');
    connectWebSocket();
  };

  // Get connection status info
  const getConnectionInfo = () => ({
    connected: connectionStatus === 'connected',
    status: connectionStatus,
    attempts: reconnectAttempts,
    websocketAvailable: !!websocket
  });

  const value = {
    processingJobs: Array.from(processingJobs.values()),
    startProcessing,
    updateJobStatus,
    updateInstanceProgress,
    getJobStatus,
    removeJob,
    cancelProcessing, // Add cancelProcessing function
    getActiveJobs,
    getAllJobs,
    getStageInfo,
    pollJobStatus,
    reconnectWebSocket,
    getConnectionInfo,
    websocketConnected: !!websocket,
    connectionStatus,
    reconnectAttempts,
    PROCESSING_STAGES
  };

  return (
    <ProcessingContext.Provider value={value}>
      {children}
    </ProcessingContext.Provider>
  );
};

export default ProcessingProvider;
